import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const GrandEntrance = ({ onEnter }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 500);
    const t2 = setTimeout(() => setPhase(2), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div data-testid="grand-entrance" className="fixed inset-0 overflow-hidden" style={{ background: '#FDFCFB' }}>
      {/* Soft ambient background image (sunlit dome / classical interior) */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762419757238-28ffcd7e034c?w=1920&q=80)',
          opacity: 0.12,
          filter: 'saturate(0.7)',
        }}
      />

      {/* Warm radial light from top center */}
      <motion.div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full"
        style={{ background: 'radial-gradient(ellipse, rgba(184,134,11,0.08) 0%, rgba(218,165,32,0.03) 40%, transparent 70%)' }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: phase >= 1 ? 1 : 0, scale: phase >= 1 ? 1 : 0.8 }}
        transition={{ duration: 2 }}
      />

      {/* Paper texture */}
      <div className="paper-texture" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
        {/* Top ornamental line */}
        <motion.div
          className="w-px mb-8"
          style={{ background: 'linear-gradient(to bottom, transparent, #B8860B, transparent)' }}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: phase >= 1 ? 70 : 0, opacity: phase >= 1 ? 0.4 : 0 }}
          transition={{ duration: 1.3, ease: 'easeOut' }}
        />

        {/* Diamond ornament */}
        <motion.div
          className="w-2 h-2 rotate-45 mb-6"
          style={{ background: '#B8860B' }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: phase >= 1 ? 0.5 : 0, scale: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        />

        {/* Title */}
        <motion.h1
          className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-center tracking-wider leading-none"
          style={{ color: '#1A1512' }}
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: phase >= 1 ? 1 : 0, y: phase >= 1 ? 0 : 25 }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          Museum of Minds
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="font-body text-lg sm:text-xl md:text-2xl mt-4 tracking-widest"
          style={{ color: '#8C8680' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 1, delay: 1 }}
        >
          Where the greatest thinkers await
        </motion.p>

        {/* Bottom ornamental line */}
        <motion.div
          className="w-px mt-8"
          style={{ background: 'linear-gradient(to top, transparent, #B8860B, transparent)' }}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: phase >= 1 ? 70 : 0, opacity: phase >= 1 ? 0.4 : 0 }}
          transition={{ duration: 1.3, ease: 'easeOut', delay: 0.15 }}
        />

        {/* Enter button */}
        <motion.button
          data-testid="enter-museum-btn"
          className="mt-12 px-12 py-4 font-display text-xs sm:text-sm tracking-[0.3em] uppercase relative overflow-hidden cursor-pointer"
          style={{
            background: '#1A1512',
            color: '#FDFCFB',
            border: 'none',
            boxShadow: '0 8px 30px rgba(26,21,18,0.15)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
          transition={{ duration: 0.7 }}
          onClick={onEnter}
          whileHover={{ boxShadow: '0 12px 40px rgba(26,21,18,0.25)', y: -2 }}
          whileTap={{ scale: 0.97 }}
        >
          Enter the Museum
        </motion.button>

        {/* Hint */}
        <motion.p
          className="absolute bottom-8 font-ui text-[10px] tracking-[0.3em] uppercase"
          style={{ color: '#B8860B' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 0.4 : 0 }}
          transition={{ delay: 0.8 }}
        >
          Begin your journey
        </motion.p>
      </div>

      {/* Subtle vignette (very light) */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, transparent 50%, rgba(242,239,233,0.6) 100%)' }} />
    </div>
  );
};

export default GrandEntrance;
